import React from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { CheckCircle2, AlertCircle, Minus } from 'lucide-react';

function gradeColor(score) {
  if (score >= 85) return 'hsl(var(--success))';
  if (score >= 70) return 'hsl(var(--primary))';
  if (score >= 50) return 'hsl(var(--warning))';
  return 'hsl(var(--danger))';
}

export function HealthBadge({ health, size = 'md', showLabel = true }) {
  if (!health) return null;
  const { score = 0, grade = '—', signals = [], suggestion, ready_for_promote } = health;
  const color = gradeColor(score);
  const dims = size === 'sm' ? { r: 14, sw: 3, fs: 'text-[10px]', box: 32 }
            : size === 'lg' ? { r: 28, sw: 4, fs: 'text-lg', box: 64 }
            : { r: 20, sw: 3.5, fs: 'text-xs', box: 48 };
  const circumference = 2 * Math.PI * dims.r;
  const dashArray = `${(score / 100) * circumference} ${circumference}`;

  return (
    <TooltipProvider delayDuration={120}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="inline-flex items-center gap-2" data-testid="health-badge">
            <div className="relative flex items-center justify-center" style={{ width: dims.box, height: dims.box }}>
              <svg width={dims.box} height={dims.box} className="-rotate-90">
                <circle cx={dims.box / 2} cy={dims.box / 2} r={dims.r} fill="none" stroke="hsl(var(--surface-2))" strokeWidth={dims.sw} />
                <circle cx={dims.box / 2} cy={dims.box / 2} r={dims.r} fill="none" stroke={color} strokeWidth={dims.sw} strokeDasharray={dashArray} strokeLinecap="round" style={{ transition: 'stroke-dasharray 400ms ease-out' }} />
              </svg>
              <span className={`absolute ${dims.fs} font-semibold tabular`} style={{ color }}>{score}</span>
            </div>
            {showLabel && (
              <div className="leading-tight">
                <div className="text-[10px] uppercase tracking-wider text-[hsl(var(--text-3))]">Health</div>
                <div className={`text-xs font-medium ${ready_for_promote ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--text-2))]'}`}>
                  Grade {grade}
                </div>
              </div>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="max-w-sm">
          <div className="space-y-1.5 text-xs">
            <div className="font-medium mb-2">Stealth health: <span style={{ color }}>{score}/100 · {grade}</span></div>
            {signals.map((s, i) => (
              <div key={i} className="flex items-start gap-2">
                {s.ok === true ? <CheckCircle2 className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--success))] mt-0.5" />
                  : s.ok === false ? <AlertCircle className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--warning))] mt-0.5" />
                  : <Minus className="h-3.5 w-3.5 shrink-0 text-[hsl(var(--text-3))] mt-0.5" />}
                <div className="flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span>{s.name}</span>
                    <span className="font-mono text-[10px] text-[hsl(var(--text-3))]">+{s.points}</span>
                  </div>
                  {s.hint && <div className="text-[11px] text-[hsl(var(--text-3))]">{s.hint}</div>}
                </div>
              </div>
            ))}
            {suggestion && <div className="mt-2 pt-2 border-t border-white/5 text-[11px] text-[hsl(var(--text-2))] italic">Next: {suggestion}</div>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
